import { Controller } from "@hotwired/stimulus";

// Builder de template de bilan hebdo. Calqué sur audit-template-builder, mais
// sans scope, avec champs KPI (numeric_min/max/step/unit, is_kpi) et rich_text.
// Toutes les opérations sections/champs sont AJAX (JSON), DOM mis à jour côté
// client. Re-render du wrapper de section quand le type change (pour afficher
// la config KPI ou les options) ou quand is_kpi change (badge).
export default class extends Controller {
  static targets = ["toc", "tocList", "content", "nameInput", "descInput",
                    "sectionWrapper", "emptyState"];
  static values = {
    templateId: Number,
    templateUrl: String,
    sectionsUrl: String,
    fieldsUrl: String,
    readOnly: Boolean,
  };

  connect() {
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }

  // ── Header ───────────────────────────────────────────────────────────
  async updateTemplate() {
    if (this.readOnlyValue) return;
    const body = new FormData();
    body.append("bilan_template[nom]", this.nameInputTarget.value);
    body.append("bilan_template[description]", this.descInputTarget.value);
    await this._fetch(this.templateUrlValue, { method: "PATCH", body });
  }

  // ── Sections ───────────────────────────────────────────────────────────
  async addSection() {
    if (this.readOnlyValue) return;
    const body = new FormData();
    body.append("bilan_template_section[nom]", "Nouvelle section");
    const res = await this._fetchJson(this.sectionsUrlValue, { method: "POST", body });
    if (!res?.ok) return;
    this._appendSectionToToc(res.section);
    await this._reloadSectionWrapper(res.section.id, { showAfter: true });
  }

  async updateSection(event) {
    if (this.readOnlyValue) return;
    const id = event.currentTarget.dataset.sectionId;
    const field = event.currentTarget.dataset.field;
    const body = new FormData();
    body.append(`bilan_template_section[${field}]`, event.currentTarget.value);
    const res = await this._fetchJson(`${this.sectionsUrlValue}/${id}`, { method: "PATCH", body });
    if (!res?.ok) return;
    if (field === "nom") this._updateTocLabel(id, event.currentTarget.value);
  }

  async deleteSection(event) {
    if (this.readOnlyValue) return;
    if (!confirm("Supprimer cette section et tous ses champs ?")) return;
    const id = event.currentTarget.dataset.sectionId;
    const res = await this._fetchJson(`${this.sectionsUrlValue}/${id}`, { method: "DELETE" });
    if (!res?.ok) return;
    this._removeTocItem(id);
    this.contentTarget.querySelector(`[data-section-id="${id}"]`)?.remove();
    const next = this.sectionWrapperTargets[0];
    if (next) {
      this._showSection(next.dataset.sectionId);
      this._highlightTocItem(next.dataset.sectionId);
    } else if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.hidden = false;
    }
  }

  openSection(event) {
    const id = event.currentTarget.dataset.sectionId;
    this._showSection(id);
    this._highlightTocItem(id);
  }

  _showSection(sectionId) {
    this.sectionWrapperTargets.forEach(w => { w.hidden = w.dataset.sectionId !== String(sectionId); });
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = true;
  }

  // ── Champs ───────────────────────────────────────────────────────────
  async addField(event) {
    if (this.readOnlyValue) return;
    const sectionId = event.currentTarget.dataset.sectionId;
    const body = new FormData();
    body.append("bilan_template_field[bilan_template_section_id]", sectionId);
    body.append("bilan_template_field[label]", "Nouveau champ");
    body.append("bilan_template_field[field_type]", "text");
    body.append("bilan_template_field[required]", "false");
    const res = await this._fetchJson(this.fieldsUrlValue, { method: "POST", body });
    if (!res?.ok) return;
    await this._reloadSectionWrapper(sectionId);
    this._bumpTocCount(sectionId, +1);
  }

  async updateField(event) {
    if (this.readOnlyValue) return;
    const id = event.currentTarget.dataset.questionId;
    const field = event.currentTarget.dataset.field;
    const body = new FormData();
    body.append(`bilan_template_field[${field}]`, event.currentTarget.value);
    await this._fetchJson(`${this.fieldsUrlValue}/${id}`, { method: "PATCH", body });
  }

  // Changement de type : sauve ET bascule INSTANTANÉMENT la config visible
  // (options / numérique), sans recharger depuis le serveur.
  typeChanged(event) {
    if (this.readOnlyValue) return;
    const el = event.currentTarget;
    const type = el.value;
    const questionId = el.dataset.questionId;

    // 1) Bascule visuelle INSTANTANÉE (synchrone, avant tout réseau) : on ne
    //    dépend pas de event.currentTarget après un await, et l'UI répond même
    //    si la persistance échoue.
    //    NB : on cible `.audb-question` (le conteneur) et PAS `[data-question-id]`
    //    car le <select> lui-même porte data-question-id, donc closest()
    //    renverrait le select et le toggle ne trouverait jamais les blocs.
    const q = el.closest(".audb-question");
    if (q) {
      const opts = q.querySelector('[data-bt-config="options"]');
      const num  = q.querySelector('[data-bt-config="numeric"]');
      if (opts) opts.hidden = !["dropdown", "multiselect", "checkbox"].includes(type);
      if (num)  num.hidden  = !["number", "kpi"].includes(type);
      const badge = q.querySelector(".audb-question__type-badge");
      if (badge) badge.textContent = el.options[el.selectedIndex]?.text || type;
    }

    // 2) Persistance en arrière-plan.
    const body = new FormData();
    body.append("bilan_template_field[field_type]", type);
    this._fetchJson(`${this.fieldsUrlValue}/${questionId}`, { method: "PATCH", body });
  }

  async updateFieldToggle(event) {
    if (this.readOnlyValue) return;
    const id = event.currentTarget.dataset.questionId;
    const field = event.currentTarget.dataset.field;
    const body = new FormData();
    body.append(`bilan_template_field[${field}]`, event.currentTarget.checked ? "true" : "false");
    const res = await this._fetchJson(`${this.fieldsUrlValue}/${id}`, { method: "PATCH", body });
    if (!res?.ok) return;
    // is_kpi affecte le badge -> re-render léger.
    if (field === "is_kpi") {
      const sectionId = event.currentTarget.closest("[data-section-id]")?.dataset.sectionId;
      if (sectionId) await this._reloadSectionWrapper(sectionId);
    }
  }

  async updateOptions(event) {
    if (this.readOnlyValue) return;
    const id = event.currentTarget.dataset.questionId;
    const lines = event.currentTarget.value.split("\n").map(l => l.trim()).filter(Boolean);
    const opts = lines.map(l => ({ label: l, value: l.toLowerCase().replace(/\s+/g, "_") }));
    const body = new FormData();
    body.append("bilan_template_field[options]", JSON.stringify(opts));
    await this._fetchJson(`${this.fieldsUrlValue}/${id}`, { method: "PATCH", body });
  }

  async deleteField(event) {
    if (this.readOnlyValue) return;
    if (!confirm("Supprimer ce champ ?")) return;
    const id = event.currentTarget.dataset.questionId;
    const sectionId = event.currentTarget.closest("[data-section-id]")?.dataset.sectionId;
    const res = await this._fetchJson(`${this.fieldsUrlValue}/${id}`, { method: "DELETE" });
    if (!res?.ok) return;
    if (sectionId) {
      await this._reloadSectionWrapper(sectionId);
      this._bumpTocCount(sectionId, -1);
    }
  }

  // ── Internals ────────────────────────────────────────────────────────
  async _reloadSectionWrapper(sectionId, { showAfter = false } = {}) {
    const res = await fetch(`${this.templateUrlValue}/edit`, { headers: { "Accept": "text/html" } });
    if (!res.ok) return;
    const html = await res.text();
    const dom = new DOMParser().parseFromString(html, "text/html");
    const fresh = dom.querySelector(`.audb-section-wrapper[data-section-id="${sectionId}"]`);
    if (!fresh) return;
    const existing = this.contentTarget.querySelector(`.audb-section-wrapper[data-section-id="${sectionId}"]`);
    if (existing) {
      const wasHidden = existing.hidden;
      existing.replaceWith(fresh);
      fresh.hidden = wasHidden && !showAfter;
    } else {
      this.contentTarget.appendChild(fresh);
      fresh.hidden = !showAfter;
    }
    if (showAfter) this._showSection(sectionId);
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = true;
  }

  _appendSectionToToc(section) {
    const list = this.hasTocListTarget ? this.tocListTarget : null;
    if (!list) return;
    const li = document.createElement("li");
    li.className = "audb-toc__item";
    li.dataset.sectionId = section.id;
    li.innerHTML = `
      <button type="button" class="audb-toc__link"
              data-action="click->bilan-template-builder#openSection"
              data-section-id="${section.id}">
        <span class="audb-toc__name">${this._esc(section.nom)}</span>
        <span class="audb-toc__count">0</span>
      </button>`;
    list.appendChild(li);
  }

  _updateTocLabel(sectionId, name) {
    const el = this.tocTarget.querySelector(`[data-section-id="${sectionId}"] .audb-toc__name`);
    if (el) el.textContent = name;
  }

  _bumpTocCount(sectionId, delta) {
    const el = this.tocTarget.querySelector(`[data-section-id="${sectionId}"] .audb-toc__count`);
    if (!el) return;
    el.textContent = String(Math.max(0, parseInt(el.textContent || "0", 10) + delta));
  }

  _removeTocItem(sectionId) {
    this.tocTarget.querySelector(`[data-section-id="${sectionId}"]`)?.remove();
  }

  _highlightTocItem(sectionId) {
    this.tocTarget.querySelectorAll(".audb-toc__item--active").forEach(el => el.classList.remove("audb-toc__item--active"));
    this.tocTarget.querySelector(`[data-section-id="${sectionId}"]`)?.classList.add("audb-toc__item--active");
  }

  async _fetchJson(url, options) {
    try {
      const res = await this._fetch(url, options);
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) return null;
      return await res.json();
    } catch (_) { return null; }
  }

  async _fetch(url, options) {
    const opts = { ...options, headers: { ...(options.headers || {}), "X-CSRF-Token": this._csrf, "Accept": "application/json" } };
    return fetch(url, opts);
  }

  _esc(s) {
    return String(s ?? "").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#39;");
  }
}
